/* marquee.js - vanilla marquee utility
   API:
     - initMarquee(trackEl, options?)
     - destroyMarquee(trackEl)
     - refreshMarquee(trackEl, options?)
     - initAllMarquees(selector = ".js-marquee", options?)
*/

(() => {
  const DATA_KEY = "mqInitialized";
  const CLONE_ATTR = "data-mq-clone";

  const getOptions = (trackEl, options = {}) => {
    const root = trackEl.closest(".c-marquee");

    const defaults = {
      clones: 1,
      gap: null,
      duration: null,
      direction: "left",
      pauseOnHover: false,
      respectReducedMotion: true,
    };

    const datasetOptions = root
      ? {
          gap: root.dataset.gap ?? null,
          duration: root.dataset.duration ?? null,
          direction: root.dataset.direction ?? null,
          pauseOnHover: root.dataset.pauseOnHover === "true",
        }
      : {};

    const normalize = (o) => {
      const out = { ...o };
      for (const k in out) {
        if (out[k] === "null") out[k] = null;
        if (out[k] === "true") out[k] = true;
        if (out[k] === "false") out[k] = false;
      }
      return out;
    };

    return { ...defaults, ...normalize(datasetOptions), ...normalize(options) };
  };

  const applyStylesAndAttrs = (trackEl, opts) => {
    const cloneLength = (opts.clones ?? 0) + 1;

    trackEl.style.setProperty("--mq-clone-length", String(cloneLength));
    if (opts.gap) trackEl.style.setProperty("--mq-gap", String(opts.gap));
    if (opts.duration)
      trackEl.style.setProperty("--mq-duration", String(opts.duration));

    trackEl.setAttribute(
      "data-direction",
      opts.direction === "right" ? "right" : "left"
    );
    trackEl.setAttribute("data-pause-on-hover", String(!!opts.pauseOnHover));
  };

  const buildClones = (trackEl, opts) => {
    const listEl = trackEl.querySelector(".js-marquee__list");
    const itemEls = trackEl.querySelectorAll(".js-marquee__item");
    if (!listEl || !itemEls.length) return;

    const frag = document.createDocumentFragment();
    const times = Math.max(0, Number(opts.clones) || 0);

    for (let i = 0; i < times; i++) {
      itemEls.forEach((el) => {
        const dup = el.cloneNode(true);
        dup.setAttribute("aria-hidden", "true");
        dup.setAttribute(CLONE_ATTR, "true");
        frag.appendChild(dup);
      });
    }
    listEl.appendChild(frag);
  };

  const destroyMarquee = (trackEl) => {
    if (!trackEl?.dataset[DATA_KEY]) return;

    const listEl = trackEl.querySelector(".js-marquee__list");
    if (listEl) {
      listEl
        .querySelectorAll(`[${CLONE_ATTR}="true"]`)
        .forEach((el) => el.remove());
    }

    trackEl.style.removeProperty("--mq-clone-length");
    trackEl.style.removeProperty("--mq-gap");
    trackEl.style.removeProperty("--mq-duration");
    trackEl.removeAttribute("data-direction");
    trackEl.removeAttribute("data-pause-on-hover");
    trackEl.removeAttribute("data-state");

    delete trackEl.dataset[DATA_KEY];
  };

  const initMarquee = (trackEl, options = {}) => {
    if (!(trackEl instanceof Element)) return;

    const opts = getOptions(trackEl, options);
    if (
      opts.respectReducedMotion &&
      window.matchMedia &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches
    ) {
      destroyMarquee(trackEl);
      trackEl.setAttribute("data-state", "disabled");
      return;
    }

    if (trackEl.dataset[DATA_KEY]) return;

    const listEl = trackEl.querySelector(".js-marquee__list");
    const itemEls = trackEl.querySelectorAll(".js-marquee__item");
    if (!listEl || !itemEls.length) return;

    applyStylesAndAttrs(trackEl, opts);
    buildClones(trackEl, opts);

    trackEl.setAttribute("data-state", "ready");
    trackEl.dataset[DATA_KEY] = "1";
  };

  const refreshMarquee = (trackEl, options = {}) => {
    destroyMarquee(trackEl);
    initMarquee(trackEl, options);
  };

  const initAllMarquees = (selector = ".js-marquee", options = {}) => {
    document
      .querySelectorAll(selector)
      .forEach((el) => initMarquee(el, options));
  };

  const ready = () =>
    initAllMarquees(".js-marquee", {
      clones: 1,
      duration: "20s",
      direction: "left",
      pauseOnHover: true,
      respectReducedMotion: false,
    });

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", ready, { once: true });
  } else {
    ready();
  }

  window.Marquee = {
    init: initMarquee,
    initAll: initAllMarquees,
    destroy: destroyMarquee,
    refresh: refreshMarquee,
  };
})();
